import { v4 as uuidv4 } from "uuid";
import { AI, COMPLIANT, NON_COMPLIANT } from "src/constants/constants";
import type { InspectionResult, AnalyzedResult, Question, ResultDisplayGroup } from "../types";

/**
 * Creates an inspection result when accepting AI's answer
 * 
 * @param inspectionResult - Current inspection result object
 * @param activeQuestionId - ID of the question being answered
 * @param currentAnalyzedResult - AI analysis result
 * @param capturedImageSrc - URL of the captured image
 * @param evidenceFile - Image file or file path
 * @returns New inspection result with AI answer accepted
 */
export const createAIAcceptedResult = (
  inspectionResult: InspectionResult,
  activeQuestionId: string,
  currentAnalyzedResult: AnalyzedResult,
  capturedImageSrc: string,
  evidenceFile: File | string
): InspectionResult => {
  return {
    ...inspectionResult,
    resultId: uuidv4(),
    confirmedResult: AI,
    questionId: activeQuestionId,
    aiResult: currentAnalyzedResult.aiResult as 'compliant' | 'non_compliant' | 'PENDING',
    isCompliant: currentAnalyzedResult.aiResult !== NON_COMPLIANT,
    evidence: [
      ...inspectionResult.evidence,
      {
        evidenceId: uuidv4(),
        uri: capturedImageSrc,
        file: evidenceFile,
        fileType: "IMAGE_JPEG",
      },
    ],
  };
};

/**
 * Creates an inspection result when overriding AI's answer
 * 
 * @param inspectionResult - Current inspection result object
 * @param activeQuestionId - ID of the question being answered
 * @param currentAnalyzedResult - AI analysis result being overridden
 * @param capturedImageSrc - URL of the captured image
 * @param evidenceFile - Image file or file path
 * @param reasonCode - Reason code for the override
 * @returns New inspection result with manual override
 */
export const createOverrideResult = (
  inspectionResult: InspectionResult,
  activeQuestionId: string,
  currentAnalyzedResult: AnalyzedResult,
  capturedImageSrc: string,
  evidenceFile: File | string,
  reasonCode: string
): InspectionResult => {
  return {
    ...inspectionResult,
    resultId: uuidv4(),
    confirmedResult:
      currentAnalyzedResult.aiResult === NON_COMPLIANT
        ? COMPLIANT
        : NON_COMPLIANT,
    questionId: activeQuestionId,
    aiResult: currentAnalyzedResult.aiResult as 'compliant' | 'non_compliant' | 'PENDING',
    isCompliant: currentAnalyzedResult.aiResult === NON_COMPLIANT,
    isInspectorOverride: true,
    overrideReason: reasonCode,
    evidence: [
      ...inspectionResult.evidence,
      {
        evidenceId: uuidv4(),
        uri: capturedImageSrc,
        file: evidenceFile,
        fileType: "IMAGE_JPEG",
      },
    ],
  };
};

/**
 * Groups results by question, then by evidence URI for display
 * Returns hierarchical structure: Question -> Instances
 * 
 * This is useful for displaying results organized by question,
 * with multiple instances per question (e.g., multiple drums inspected).
 * 
 * @param results - Array of inspection results
 * @param questions - Array of questions from the template
 * @returns Array of grouped results ready for display
 */
export const groupResultsForDisplay = (
  results: InspectionResult[],
  questions: Question[]
): ResultDisplayGroup[] => {
  const grouped: {
    [questionId: string]: {
      questionText: string;
      questionOrder: number;
      instances: Array<{
        instanceNumber: number;
        evidenceUri: string;
        result: InspectionResult;
      }>;
    };
  } = {};

  results.forEach((result) => {
    const questionId = result.questionId;

    if (!grouped[questionId]) {
      const question = questions.find((q) => q.questionId === questionId);
      const questionIndex = question ? questions.indexOf(question) : -1;

      grouped[questionId] = {
        questionText: question?.questionText || "",
        questionOrder: questionIndex >= 0 ? questionIndex + 1 : 0,
        instances: [],
      };
    }

    // Handle evidence safely - PENDING results might not have proper evidence yet
    const evidenceUri =
      result.evidence?.[0]?.uri ||
      (result.aiResult === "PENDING" ? `pending-${result.resultId}` : "unknown");

    // Check if this evidence URI already exists for this question
    const existingInstance = grouped[questionId].instances.find(
      (i) => i.evidenceUri === evidenceUri
    );

    if (!existingInstance) {
      grouped[questionId].instances.push({
        instanceNumber: grouped[questionId].instances.length + 1,
        evidenceUri,
        result,
      });
    }
  });

  // Convert to array and sort by question order
  return Object.entries(grouped)
    .map(([questionId, data]) => ({
      questionId,
      ...data,
    }))
    .sort((a, b) => a.questionOrder - b.questionOrder);
};